import React, { useCallback, useEffect, useReducer } from 'react';
import { Button, Stack, Overlay, Text, LoadingBalls } from '@moda/om';
import loadable from '@loadable/component';
import { useForm } from '@moda/portal-stanchions';
import { reportError } from '../../../lib/reportError';
import { isGenericError } from '../../../lib/error';
import { createUserApiError } from '../../../lib/createUserApiError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import {
  useCreateAddressMutation,
  useEditAddressMutation,
  RestAddressAttributes,
  RestAddress
} from '../../../hooks/useAddress';
import { useFormValidationTracking } from '../../../hooks/useCheckout/useFormValidationTracking';
import { FormButtonsLayout } from '../../FormButtonsLayout';
import { ShippingAddressFormFields } from './ShippingAddressFormFields';
import { useAddressState } from './useAddressState';
import { getValidations } from './getValidations';

interface Props {
  address?: RestAddress;
  submitButtonLabel?: string;
  onCancel?: () => void;
  onSave: (address?: RestAddress) => void | Promise<void>;
}

enum Mode {
  Resting,
  Validating,
  Saving
}

type State = {
  mode: Mode;
  attributesToValidate?: RestAddressAttributes;
};

type Action =
  | { type: 'START_VALIDATING'; payload: RestAddressAttributes }
  | { type: 'STOP_VALIDATING' }
  | { type: 'START_SAVING' }
  | { type: 'STOP_SAVING' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'START_VALIDATING':
      return {
        mode: Mode.Validating,
        attributesToValidate: action.payload
      };
    case 'STOP_VALIDATING':
      return { mode: Mode.Resting };
    case 'START_SAVING':
      return { mode: Mode.Saving };
    case 'STOP_SAVING':
      return { mode: Mode.Resting };
    default:
      return state;
  }
};

const ShippingAddressValidationPopup = loadable(() => import('../ShippingAddressValidationPopup'), {
  resolveComponent: exports => exports.ShippingAddressValidationPopup
});

export const ShippingAddressForm: React.FC<Props> = ({
  address,
  submitButtonLabel = 'Save',
  onSave,
  onCancel
}) => {
  const { showErrorModal } = useErrorModal();

  const [createAddress, { error: createError }] = useCreateAddressMutation();
  const [editAddress, { error: editError }] = useEditAddressMutation();
  const { trackFormValidation } = useFormValidationTracking();

  const [state, dispatch] = useReducer(reducer, { mode: Mode.Resting });
  const { mode, attributesToValidate } = state;

  const startValidation = useCallback((attributes: RestAddressAttributes) => {
    dispatch({ type: 'START_VALIDATING', payload: attributes });
  }, []);

  const stopValidation = useCallback(() => {
    dispatch({ type: 'STOP_VALIDATING' });
  }, []);

  const [addressAttributes, setAddressAttributes] = useAddressState(address);

  const {
    handleSubmit: handleSubmitForValidation,
    errors: validationErrors,
    hasErrors
  } = useForm({
    onSubmit: startValidation,
    validations: getValidations(addressAttributes)
  });

  const handleSubmit = useCallback(
    async (attributes: RestAddressAttributes) => {
      dispatch({ type: 'START_SAVING' });

      try {
        const result = address
          ? await editAddress({ ...address, attributes })
          : await createAddress(attributes);
        await onSave(result);
      } catch (error) {
        if (!isGenericError(error)) return;
        const userApiError = createUserApiError(error);
        if (userApiError && (Object.keys(userApiError).length === 0 || userApiError.network)) {
          showErrorModal(error.toString());
          reportError('SHIPPING_ADDRESS_FORM_SUBMIT', error);
        }
      }

      dispatch({ type: 'STOP_SAVING' });
    },
    [address, createAddress, editAddress, showErrorModal, onSave]
  );

  useEffect(() => {
    if (!hasErrors) return;

    trackFormValidation('Shipping Address Form Validation Error', validationErrors);
  }, [hasErrors, validationErrors, trackFormValidation]);

  return (
    <>
      <form className="ShippingAddressForm" onSubmit={handleSubmitForValidation}>
        <Stack space={4}>
          <ShippingAddressFormFields
            address={addressAttributes}
            onChange={setAddressAttributes}
            mutationFieldErrors={{ ...createError, ...editError }}
            validationErrors={validationErrors}
          />

          <FormButtonsLayout>
            {onCancel && (
              <Button
                data-test-id="shipping-address-form--continue"
                onClick={onCancel}
                secondary
                type="button"
              >
                Cancel
              </Button>
            )}

            <Button data-test-id={`shipping-address-form--${submitButtonLabel}`} type="submit">
              {submitButtonLabel}
            </Button>
          </FormButtonsLayout>
        </Stack>
      </form>

      {mode === Mode.Validating && attributesToValidate && (
        <ShippingAddressValidationPopup
          attributes={attributesToValidate}
          requireConfirmationIfVerified
          onSubmit={handleSubmit}
          onClose={stopValidation}
        />
      )}

      <Overlay show={mode === Mode.Saving || mode === Mode.Validating}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
